import { tsvParse, csvParse } from "d3-dsv";
import { json } from "d3";
import { timeParse } from "d3-time-format";

const API_BASE =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : "http://localhost:49152";

function authHeaders() {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authFetch(url, options = {}) {
  const headers = { ...authHeaders(), ...(options.headers || {}) };
  return fetch(url, { ...options, headers }).then((resp) => {
    if (resp.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.hash = "#/";
      window.location.reload();
      throw new Error("Session expired");
    }
    return resp;
  });
}

function parseData(parse) {
  return function (d) {
    d.date = parse(d.date);
    d.open = +d.open;
    d.high = +d.high;
    d.low = +d.low;
    d.close = +d.close;
    d.volume = +d.volume;

    return d;
  };
}

const parseDate = timeParse("%Y-%m-%d %H:%M:%S");

export function getCandleData() {
  const promiseMSFT = fetch(
    "https://cdn.rawgit.com/rrag/react-stockcharts/master/docs/data/MSFT.tsv"
  )
    .then((response) => response.text())
    .then((data) => tsvParse(data, parseData(parseDate)));
  return promiseMSFT;
}

export function getHomePageTableData() {
  const dataPromise = new Promise((resolve, reject) => {
    // Simulating an asynchronous operation (e.g., fetching data from an API)
    setTimeout(() => {
      const dummyData = [
        {
          ticker: "AAPL",
          last: 150.25,
          change: 2.75,
          volume: 1000000,
          signal: "Buy",
        },
        {
          ticker: "GOOGL",
          last: 2800.5,
          change: -15.25,
          volume: 500000,
          signal: "Sell",
        },
        {
          ticker: "IBM",
          last: 310.75,
          change: 1.2,
          volume: 750000,
          signal: "Hold",
        },
        // Add more dummy data as needed
      ];
      resolve(dummyData);
    }, 1000); // Simulating a 1-second delay, replace with your actual async operation time
  });
  return dataPromise;
}

export function getListOfStocksInSignal() {
  const dataPromise = new Promise((resolve, reject) => {
    // Simulating an asynchronous operation (e.g., fetching data from an API)
    setTimeout(() => {
      const dummyData = ["AAPL", "IBM", "GOOGL"];
      resolve(dummyData);
    }, 1000); // Simulating a 1-second delay, replace with your actual async operation time
  });
  return dataPromise;
}

export function getStockDetails(name) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  const apiKey = process.env.REACT_APP_FMP_KEY;
  return fetch(
    `https://financialmodelingprep.com/api/v3/profile/${name}?apikey=${apiKey}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      const finalData = {
        symbol: name,
        company: result[0]["companyName"],
        country: result[0]["country"],
        industry: result[0]["industry"],
        exchange: result[0]["exchange"],
      };
      return finalData;
    })
    .catch((error) => console.log("error", error));
}

export function getStockNews(name) {
  const apiKey = process.env.REACT_APP_FMP_KEY;

  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };
  return fetch(
    `https://financialmodelingprep.com/api/v3/stock_news?page=0&limit=10&tickers=${name}&apikey=${apiKey}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => result)
    .catch((error) => console.log("error", error));
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDates(N) {
  const currentDate = new Date();
  const previousDate = new Date(currentDate);
  previousDate.setDate(currentDate.getDate() - N);

  const currentDateString = formatDate(currentDate);
  const previousDateString = formatDate(previousDate);

  return {
    currentDate: currentDateString,
    previousDate: previousDateString,
  };
}

function getPeriod(days) {
  if (days / 365 >= 1) {
    return `${days / 365}y`;
  }
  if (days / 30 >= 1) {
    return `${days / 30}mo`;
  } else {
    return `${days}d`;
  }
}
export function getIntraDayData(name, days = "1") {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  const daysToIntervalMapping = {
    1: "1m",
    3: "5m",
    5: "5m",
    7: "5m",
    30: "1h",
    90: "1d",
    180: "1d",
    365: "1d",
    730: "1wk",
    1825: "1mo",
    3650: "1mo",
  };

  const apiKey = process.env.REACT_APP_FMP_KEY;

  let url = `${API_BASE}/daily_chart?symbol=${name}&period=max&interval=1wk`;
  if (days !== "max") {
    const period = getPeriod(days);
    const interval = daysToIntervalMapping[days];
    url = `${API_BASE}/daily_chart?symbol=${name}&period=${period}&interval=${interval}`;
  }

  return authFetch(url, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      const finalData = [];
      result["historical"].forEach((element) => {
        const d = {};
        d.date = parseDate(element["date"]);
        d.open = element["open"];
        d.close = element["close"];
        d.low = element["low"];
        d.high = element["high"];
        d.volume = element["volume"];
        d.adx = element["adx"];
        d.dmi_plus = element["dmi_plus"];
        d.dmi_minus = element["dmi_minus"];
        d.roc5 = element["roc5"];
        d.roc20 = element["roc20"];
        d.bb_width = element["bb_width"];
        finalData.push(d);
      });
      return finalData;
    })
    .catch((error) => {
      console.log("error", error);
      return []
    });
}

export function getPairChartData(name1, name2, days = "1") {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  const daysToIntervalMapping = {
    1: "1m",
    3: "5m",
    5: "5m",
    7: "5m",
    30: "1h",
    90: "1d",
    180: "1d",
    365: "1d",
    730: "1wk",
    1825: "1mo",
    3650: "1mo",
  };

  const apiKey = process.env.REACT_APP_FMP_KEY;

  let url = `${API_BASE}/compare?symbol1=${name1}&symbol2=${name2}&period=max&interval=1wk`;
  if (days !== "max") {
    const period = getPeriod(days);
    const interval = daysToIntervalMapping[days];
    url = `${API_BASE}/compare?symbol1=${name1}&symbol2=${name2}&period=${period}&interval=${interval}`;
  }

  return authFetch(url, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      const finalData = [];
      result["historical"].forEach((element) => {
        const d = {};
        d.date = parseDate(element["date"]);
        d.open = element["open"];
        d.close = element["close"];
        d.low = element["low"];
        d.high = element["high"];
        d[`Open_${name1}`] = element[`Open_${name1}`];
        d[`Open_${name2}`] = element[`Open_${name2}`];
        d[`Close_${name1}`] = element[`Close_${name1}`];
        d[`Close_${name2}`] = element[`Close_${name2}`];
        d[`Low_${name1}`] = element[`Low_${name1}`];
        d[`Low_${name2}`] = element[`Low_${name2}`];
        d[`High_${name1}`] = element[`High_${name1}`];
        d[`High_${name2}`] = element[`High_${name2}`];
        // d.adx = element["adx"];
        // d.dmi_plus = element["dmi_plus"];
        // d.dmi_minus = element["dmi_minus"];
        // d.roc5 = element["roc5"];
        // d.roc20 = element["roc20"];
        // d.bb_width = element["bb_width"];
        finalData.push(d);
      });
      return finalData.reverse();
    })
    .catch((error) => console.log("error", error));
}

export function screenerData(filters) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  const apiKey = process.env.REACT_APP_FMP_KEY;

  return fetch(
    `https://financialmodelingprep.com/api/v3/stock-screener?apikey=${apiKey}&${filters}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => result)
    .catch((error) => console.log("error", error));
}

export function getHomeDashboardStocks() {
  const dataPromise = new Promise((resolve, reject) => {
    // Simulating an asynchronous operation (e.g., fetching data from an API)
    setTimeout(() => {
      const stocks = [
        { name: "IBM" },
        { name: "AAPL" },
        { name: "GOOGL" },
        { name: "MSFT" },
        { name: "ORCL" },
        { name: "SAP" },
        { name: "NOW" },
        { name: "UBER" },
        { name: "PANW" },
      ];
      resolve(stocks);
    }, 1000); // Simulating a 1-second delay, replace with your actual async operation time
  });
  return dataPromise;
}

export function getPriceDeltaValues(name) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };
  const url = `${API_BASE}/daily_chart?symbol=${name}&period=2mo&interval=1d`;

  return authFetch(url, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      const data = result["historical"];
      // Calculate the last 1 day price change
      const last1DayChange = (data[0].close - data[1].close).toFixed(3);

      // Calculate the 1 week price change
      const lastWeekClose = data[0].close;
      const oneWeekAgoClose = data.find(
        (item) =>
          new Date(item.date) < new Date(data[0].date) - 7 * 24 * 60 * 60 * 1000
      ).close;
      const oneWeekChange = (lastWeekClose - oneWeekAgoClose).toFixed(3);

      // Calculate the 1 month price change
      const lastMonthClose = data[0].close;
      const oneMonthAgoClose = data.find(
        (item) =>
          new Date(item.date) <
          new Date(data[0].date) - 30 * 24 * 60 * 60 * 1000
      ).close;
      const oneMonthChange = (lastMonthClose - oneMonthAgoClose).toFixed(3);

      const delta = [
        {
          name: "1day",
          value: last1DayChange,
        },
        {
          name: "1week",
          value: oneWeekChange,
        },
        {
          name: "1month",
          value: oneMonthChange,
        },
      ];
      return delta;
    })
    .catch((error) => console.log("error", error));
}

function revenueConversion(revenue) {
  if (revenue >= 1000000000) {
    return revenue / 1000000000 + "B";
  } else if (revenue >= 1000000) {
    return revenue / 1000000 + "M";
  } else {
    return revenue;
  }
}

export function getEarnings(name) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };
  const apiKey = process.env.REACT_APP_FMP_KEY;

  return fetch(
    `https://financialmodelingprep.com/api/v3/historical/earning_calendar/${name}?apikey=${apiKey}&limit=10`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      for (let item of result) {
        if (item.revenue !== null) {
          return revenueConversion(item.revenue);
        }
      }
      return "NA";
    })
    .catch((error) => {
      console.log(error);
    });
}

export function getCompanyInformation(name) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(
    `${API_BASE}/company_profile?symbol=${name}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      // console.log(result);
      return {
        name: result["longName"],
        sector: result["sector"],
        revenue: result["totalRevenue"],
        country: result["country"],
      };
    })
    .catch((error) => {
      console.log(error);
    });
}

export function getTreemapData() {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(`${API_BASE}/treemap`, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      // console.log(result);
      return result;
    })
    .catch((error) => {
      console.log(error);
    });
}

export function getTreemapSymbols(name, category) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(
    `${API_BASE}/treemap_symbols?name=${name}&category=${category}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      // console.log(result);
      return result;
    })
    .catch((error) => {
      console.log(error);
    });
}

export function getPortfolio(name, category) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(
    `${API_BASE}/portfolio`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      // console.log(result);
      return result;
    })
    .catch((error) => {
      console.log(error);
    });
}

export function getAvailableSymbols() {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(`${API_BASE}/symbols`, requestOptions)
    .then((response) => response.json())
    .then((result) => result)
    .catch((error) => {
      console.log(error);
      return [];
    });
}

export function getIndexPresets() {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(`${API_BASE}/index-presets`, requestOptions)
    .then((response) => response.json())
    .then((result) => result)
    .catch((error) => {
      console.log(error);
      return {};
    });
}

export function getIndexSymbols(presetName) {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(`${API_BASE}/index-symbols/${presetName}`, requestOptions)
    .then((response) => response.json())
    .then((result) => result)
    .catch((error) => {
      console.log(error);
      return [];
    });
}

export function getBacktestData(params) {
  const query = new URLSearchParams(params).toString();
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(
    `${API_BASE}/backtest?${query}`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      return result;
    })
    .catch((error) => {
      console.log(error);
      return null;
    });
}

export function getBacktestHistory(limit = 50) {
  return authFetch(`${API_BASE}/backtest/history?limit=${limit}`, { method: "GET" })
    .then((response) => response.json())
    .catch((error) => { console.log(error); return []; });
}

export function getBacktestHistoryItem(runId) {
  return authFetch(`${API_BASE}/backtest/history/${runId}`, { method: "GET" })
    .then((response) => response.json())
    .catch((error) => { console.log(error); return null; });
}

export function deleteBacktestRun(runId) {
  return authFetch(`${API_BASE}/backtest/history/${runId}`, { method: "DELETE" })
    .then((response) => response.json())
    .catch((error) => { console.log(error); return null; });
}

export function renameBacktestRun(runId, newName) {
  return authFetch(`${API_BASE}/backtest/history/${runId}/rename`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: newName }),
  })
    .then((response) => response.json())
    .catch((error) => { console.log(error); return null; });
}

export function getSignalData() {
  var requestOptions = {
    method: "GET",
    redirect: "follow",
  };

  return authFetch(
    `${API_BASE}/signal`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      // console.log(result);
      return result;
    })
    .catch((error) => {
      console.log(error);
      return null;
    });
}

// Data Sync
export async function triggerDataSync(preset = "signal_stocks", years = 5) {
  const response = await authFetch(`${API_BASE}/data/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ preset, years }),
  });
  return response.json();
}

export async function getDataSyncStatus() {
  const response = await authFetch(`${API_BASE}/data/sync-status`);
  return response.json();
}

export async function getDataStats() {
  const response = await authFetch(`${API_BASE}/data/stats`);
  return response.json();
}

// Signals
export async function triggerSignalScan(
  preset = "signal_stocks",
  take_profit = 5,
  stop_loss = 2
) {
  const response = await authFetch(`${API_BASE}/signals/scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ preset, take_profit, stop_loss }),
  });
  return response.json();
}

export async function getSignalsToday() {
  const response = await authFetch(`${API_BASE}/signals/today`);
  return response.json();
}

export async function getSignalsHistory(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const response = await authFetch(
    `${API_BASE}/signals/history?${queryString}`
  );
  return response.json();
}

// Scheduler
export async function getSchedulerStatus() {
  const response = await authFetch(`${API_BASE}/scheduler/status`);
  return response.json();
}

export async function triggerSchedulerJob(jobId) {
  const response = await authFetch(
    `${API_BASE}/scheduler/jobs/${jobId}/run`,
    {
      method: "POST",
      headers: authHeaders(),
    }
  );
  return response.json();
}

// Paper Trading
export async function getPaperTrades() {
  const response = await authFetch(`${API_BASE}/paper/trades`);
  return response.json();
}

export async function getPaperMetrics() {
  const response = await authFetch(`${API_BASE}/paper/metrics`);
  return response.json();
}

export async function getPaperEquity() {
  const response = await authFetch(`${API_BASE}/paper/equity`);
  return response.json();
}

export async function triggerPaperScan() {
  const response = await authFetch(`${API_BASE}/paper/scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  return response.json();
}

// Backfill
export async function triggerBackfill(instrument = "all", start, end) {
  const body = { instrument };
  if (start) body.start = start;
  if (end) body.end = end;
  const response = await authFetch(`${API_BASE}/data/backfill`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  return response.json();
}

export async function getBackfillStatus() {
  const response = await authFetch(`${API_BASE}/data/backfill-status`);
  return response.json();
}

// ─── Kite Connect APIs ───────────────────────────────────────────────

export function getKiteLoginUrl() {
  return `${API_BASE}/api/kite/login`;
}

export async function getKiteStatus() {
  const response = await authFetch(`${API_BASE}/api/kite/status`);
  return response.json();
}

export async function triggerKiteSync(
  symbol = "^NSEI",
  intervals = ["1m", "5m", "daily"],
  days
) {
  const body = { symbol, intervals };
  if (days) body.days = days;
  const response = await authFetch(`${API_BASE}/api/kite/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  return response.json();
}

export async function getKiteSyncStatus() {
  const response = await authFetch(`${API_BASE}/api/kite/sync-status`);
  return response.json();
}

export async function triggerKiteBackfill(
  symbol = "^NSEI",
  intervals = ["1m", "5m", "daily"]
) {
  const response = await authFetch(`${API_BASE}/api/kite/backfill`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ symbol, intervals }),
  });
  return response.json();
}
